/**
 * Co-Pilot Settings Tab Component
 * Manages LLM provider configuration and MCP server settings for Converse Co-Pilot v4.0
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Slider,
  Switch
} from '@mui/material';
import {
  Save,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  Settings,
  Storage,
  Psychology,
  Code,
  School,
  Refresh
} from '@mui/icons-material';

interface LLMProvider {
  id: string;
  name: string;
  models: {
    id: string;
    name: string;
    context_window: number;
    supports_tools: boolean;
  }[];
}

interface MCPServer {
  id: number;
  name: string;
  description?: string;
  config: any;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface CoPilotSettings {
  enabled: boolean;
  llm_provider: string;
  llm_model: string;
  api_key?: string;  // For sending to backend
  api_key_configured?: boolean;  // From backend (readonly)
  temperature: number;
  max_tokens: number;
  system_prompt?: string;
}

interface KnowledgeBaseSettings {
  embedding_provider: string;
  embedding_model: string;
  embedding_dimension: number;
  openai_model?: string;
  openai_api_key?: string;
  has_openai_key?: boolean;
  max_chunk_tokens: number;
  chunk_overlap_tokens: number;
  batch_size: number;
  is_configured: boolean;
  last_updated?: string;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const CoPilotSettingsTab: React.FC = () => {
  // LLM Settings State
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [settings, setSettings] = useState<CoPilotSettings>({
    enabled: true,
    llm_provider: 'anthropic',
    llm_model: 'claude-3-5-sonnet-20241022',
    temperature: 0.7,
    max_tokens: 4096
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // MCP Servers State
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [showMcpDialog, setShowMcpDialog] = useState(false);
  const [editingMcp, setEditingMcp] = useState<MCPServer | null>(null);
  const [mcpForm, setMcpForm] = useState({
    name: '',
    description: '',
    config: '{\n  "command": "npx",\n  "args": ["-y", "@package/server"],\n  "env": {}\n}'
  });

  // Knowledge Base Settings State
  const [kbSettings, setKbSettings] = useState<KnowledgeBaseSettings>({
    embedding_provider: 'local',
    embedding_model: 'paraphrase-MiniLM-L3-v2',  // Lightweight model (61MB)
    embedding_dimension: 384,
    max_chunk_tokens: 512,
    chunk_overlap_tokens: 50,
    batch_size: 16,
    is_configured: false
  });
  const [kbLoading, setKbLoading] = useState(false);
  const [kbSaving, setKbSaving] = useState(false);

  // UI State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [testingServer, setTestingServer] = useState<number | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [refreshingTools, setRefreshingTools] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadProviders();
    loadCoPilotSettings();
    loadMcpServers();
    loadKBSettings();
  }, []);

  // Fetch models dynamically when API key changes (for providers that support it)
  useEffect(() => {
    if (settings.api_key && settings.api_key.trim() !== '') {
      fetchDynamicModels();
    }
  }, [settings.api_key, settings.llm_provider]);

  const loadProviders = async () => {
    try {
      const response = await fetch(`${API_BASE}/copilot-settings/providers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadCoPilotSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/copilot-settings/`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading Co-Pilot settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchDynamicModels = async () => {
    if (!settings.llm_provider || !settings.api_key || settings.api_key.trim() === '') {
      return;
    }

    setLoadingModels(true);
    try {
      const params = new URLSearchParams({
        api_key: settings.api_key
      });

      const response = await fetch(
        `${API_BASE}/copilot-settings/models/${settings.llm_provider}?${params}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        // Update the provider's models in the providers state
        setProviders(prevProviders =>
          prevProviders.map(provider =>
            provider.id === settings.llm_provider
              ? { ...provider, models: data.models }
              : provider
          )
        );
      }
    } catch (error) {
      console.error('Error fetching dynamic models:', error);
      // Silently fail and use static models
    } finally {
      setLoadingModels(false);
    }
  };

  const saveCoPilotSettings = async () => {
    setSettingsSaving(true);
    try {
      // Only send fields that backend accepts (exclude api_key_configured)
      const updatePayload = {
        enabled: settings.enabled,
        llm_provider: settings.llm_provider,
        llm_model: settings.llm_model,
        api_key: settings.api_key,
        temperature: settings.temperature,
        max_tokens: settings.max_tokens,
        system_prompt: settings.system_prompt
      };

      const response = await fetch(`${API_BASE}/copilot-settings/`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Co-Pilot settings saved successfully!', severity: 'success' });
      } else {
        const errorData = await response.json();
        console.error('Error saving Co-Pilot settings:', errorData);
        throw new Error(errorData.detail || 'Failed to save settings');
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save Co-Pilot settings', severity: 'error' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadMcpServers = async () => {
    setMcpLoading(true);
    try {
      const response = await fetch(`${API_BASE}/mcp-servers/`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMcpServers(data);
      }
    } catch (error) {
      console.error('Error loading MCP servers:', error);
    } finally {
      setMcpLoading(false);
    }
  };

  const saveMcpServer = async () => {
    try {
      // Parse and validate config JSON
      let configJson;
      try {
        configJson = JSON.parse(mcpForm.config);
      } catch {
        setSnackbar({ open: true, message: 'Invalid JSON configuration', severity: 'error' });
        return;
      }

      const endpoint = editingMcp
        ? `${API_BASE}/mcp-servers/${editingMcp.id}`
        : `${API_BASE}/mcp-servers/`;

      const method = editingMcp ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: mcpForm.name,
          description: mcpForm.description,
          config: configJson,
          enabled: true
        })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `MCP server ${editingMcp ? 'updated' : 'created'} successfully!`,
          severity: 'success'
        });
        setShowMcpDialog(false);
        loadMcpServers();
        resetMcpForm();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save MCP server');
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save MCP server', severity: 'error' });
    }
  };

  const deleteMcpServer = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this MCP server?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/mcp-servers/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'MCP server deleted successfully!', severity: 'success' });
        loadMcpServers();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete MCP server', severity: 'error' });
    }
  };

  const testMcpServer = async (id: number) => {
    setTestingServer(id);
    try {
      const response = await fetch(`${API_BASE}/mcp-servers/${id}/test`, {
        method: 'POST',
        credentials: 'include'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSnackbar({
          open: true,
          message: `Connection successful! ${result.tools_available} tools available.`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.message || 'Connection test failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to test MCP server connection', severity: 'error' });
    } finally {
      setTestingServer(null);
    }
  };

  // 03/12/2025 - Added toggle enable/disable functionality
  const toggleMcpServerEnabled = async (server: MCPServer) => {
    try {
      const response = await fetch(`${API_BASE}/mcp-servers/${server.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: !server.enabled
        })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `MCP server ${!server.enabled ? 'enabled' : 'disabled'} successfully!`,
          severity: 'success'
        });
        loadMcpServers();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update MCP server');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update MCP server status',
        severity: 'error'
      });
    }
  };

  // 05/02/2026 - Added refresh tools functionality
  const refreshMcpTools = async () => {
    setRefreshingTools(true);
    try {
      const response = await fetch(`${API_BASE}/mcp-servers/refresh-tools`, {
        method: 'POST',
        credentials: 'include'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSnackbar({
          open: true,
          message: result.message || `Refreshed ${result.tools?.length || 0} tools from MCP servers`,
          severity: 'success'
        });
      } else {
        throw new Error(result.detail || result.message || 'Failed to refresh tools');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh MCP tools',
        severity: 'error'
      });
    } finally {
      setRefreshingTools(false);
    }
  };

  const resetMcpForm = () => {
    setMcpForm({
      name: '',
      description: '',
      config: '{\n  "command": "npx",\n  "args": ["-y", "@package/server"],\n  "env": {}\n}'
    });
    setEditingMcp(null);
  };

  const openEditDialog = (server: MCPServer) => {
    setEditingMcp(server);
    setMcpForm({
      name: server.name,
      description: server.description || '',
      config: JSON.stringify(server.config, null, 2)
    });
    setShowMcpDialog(true);
  };

  const loadKBSettings = async () => {
    setKbLoading(true);
    try {
      const response = await fetch(`${API_BASE}/knowledge-base/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setKbSettings(data);
      }
    } catch (error) {
      console.error('Error loading KB settings:', error);
    } finally {
      setKbLoading(false);
    }
  };

  const saveKBSettings = async () => {
    setKbSaving(true);
    try {
      const response = await fetch(`${API_BASE}/knowledge-base/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(kbSettings)
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Knowledge Base settings saved successfully!', severity: 'success' });
        loadKBSettings(); // Reload to get updated settings
      } else {
        throw new Error('Failed to save KB settings');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save Knowledge Base settings', severity: 'error' });
    } finally {
      setKbSaving(false);
    }
  };

  const selectedProvider = providers.find(p => p.id === settings.llm_provider);
  const availableModels = selectedProvider?.models || [];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Converse Co-Pilot v4.0
        </Typography>
        <Typography variant="body2">
          Configure the LLM provider and MCP servers for the agent-facing AI assistant.
          This is separate from the customer-facing AI Assistant (Captain AI).
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* LLM Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="LLM Configuration"
              avatar={<Psychology color="primary" />}
            />
            <CardContent>
              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>LLM Provider</InputLabel>
                    <Select
                      value={settings.llm_provider}
                      label="LLM Provider"
                      onChange={(e) => setSettings({ ...settings, llm_provider: e.target.value, llm_model: '' })}
                    >
                      {providers.map(provider => (
                        <MenuItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl fullWidth disabled={!settings.llm_provider}>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={settings.llm_model}
                        label="Model"
                        onChange={(e) => setSettings({ ...settings, llm_model: e.target.value })}
                        endAdornment={
                          loadingModels ? (
                            <CircularProgress size={20} sx={{ mr: 2 }} />
                          ) : null
                        }
                      >
                        {availableModels.map(model => (
                          <MenuItem key={model.id} value={model.id}>
                            {model.name} ({model.context_window.toLocaleString()} tokens)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {settings.api_key && settings.api_key.trim() !== '' && (
                      <Tooltip title="Refresh models from provider">
                        <IconButton
                          onClick={fetchDynamicModels}
                          disabled={loadingModels}
                          sx={{ mt: 1 }}
                        >
                          {loadingModels ? <CircularProgress size={20} /> : <Settings />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <TextField
                    label="API Key"
                    type="password"
                    value={settings.api_key || ''}
                    onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                    fullWidth
                    helperText="Your API key is encrypted and stored securely"
                  />

                  <Box>
                    <Typography gutterBottom>
                      Temperature: {settings.temperature}
                    </Typography>
                    <Slider
                      value={settings.temperature}
                      onChange={(_, value) => setSettings({ ...settings, temperature: value as number })}
                      min={0}
                      max={2}
                      step={0.1}
                      marks={[
                        { value: 0, label: '0 (Focused)' },
                        { value: 1, label: '1 (Balanced)' },
                        { value: 2, label: '2 (Creative)' }
                      ]}
                    />
                  </Box>

                  <TextField
                    label="Max Tokens"
                    type="number"
                    value={settings.max_tokens}
                    onChange={(e) => setSettings({ ...settings, max_tokens: parseInt(e.target.value) })}
                    fullWidth
                    inputProps={{ min: 100, max: 1000000 }}
                    helperText="Maximum tokens in response (up to 1M for supported models)"
                  />

                  <TextField
                    label="System Prompt (Optional)"
                    multiline
                    rows={3}
                    value={settings.system_prompt || ''}
                    onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
                    fullWidth
                    placeholder="Custom instructions for the Co-Pilot assistant..."
                  />

                  <Button
                    variant="contained"
                    startIcon={settingsSaving ? <CircularProgress size={16} /> : <Save />}
                    onClick={saveCoPilotSettings}
                    disabled={settingsSaving}
                  >
                    {settingsSaving ? 'Saving...' : 'Save LLM Settings'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* MCP Servers */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="MCP Servers"
              avatar={<Storage color="primary" />}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Refresh tool definitions from all connected MCP servers">
                    <Button
                      startIcon={refreshingTools ? <CircularProgress size={16} /> : <Refresh />}
                      onClick={refreshMcpTools}
                      disabled={refreshingTools || mcpServers.length === 0}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {refreshingTools ? 'Refreshing...' : 'Refresh Tools'}
                    </Button>
                  </Tooltip>
                  <Button
                    startIcon={<Add />}
                    onClick={() => {
                      resetMcpForm();
                      setShowMcpDialog(true);
                    }}
                    size="small"
                  >
                    Add Server
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {mcpLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : mcpServers.length === 0 ? (
                <Alert severity="info">
                  No MCP servers configured. Add your first server to enable backend data access for Co-Pilot.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mcpServers.map(server => (
                        <TableRow key={server.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {server.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {server.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {/* 03/12/2025 - Added toggle switch for enable/disable */}
                            <Tooltip title={server.enabled ? 'Click to disable' : 'Click to enable'}>
                              <Switch
                                checked={server.enabled}
                                onChange={() => toggleMcpServerEnabled(server)}
                                color="success"
                                size="small"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Test Connection">
                                <IconButton
                                  size="small"
                                  onClick={() => testMcpServer(server.id)}
                                  disabled={testingServer === server.id}
                                >
                                  {testingServer === server.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckCircle />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEditDialog(server)}>
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => deleteMcpServer(server.id)}>
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Knowledge Base Embedding Settings */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Knowledge Base Embedding Settings"
              avatar={<School color="primary" />}
            />
            <CardContent>
              {kbLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>Embedding Provider</InputLabel>
                        <Select
                          value={kbSettings.embedding_provider}
                          label="Embedding Provider"
                          onChange={(e) => setKbSettings({ ...kbSettings, embedding_provider: e.target.value })}
                        >
                          <MenuItem value="local">Local (Sentence Transformers)</MenuItem>
                          <MenuItem value="openai">OpenAI</MenuItem>
                        </Select>
                      </FormControl>

                      {kbSettings.embedding_provider === 'local' ? (
                        <FormControl fullWidth>
                          <InputLabel>Embedding Model</InputLabel>
                          <Select
                            value={kbSettings.embedding_model}
                            label="Embedding Model"
                            onChange={(e) => {
                              const model = e.target.value;
                              let dimension = 384;
                              if (model.includes('mpnet-base-v2')) dimension = 768;
                              setKbSettings({ ...kbSettings, embedding_model: model, embedding_dimension: dimension });
                            }}
                          >
                            <MenuItem value="paraphrase-MiniLM-L3-v2">
                              paraphrase-MiniLM-L3-v2 (384 dim, 61MB, fastest) ⚡ RECOMMENDED
                            </MenuItem>
                            <MenuItem value="sentence-transformers/all-MiniLM-L6-v2">
                              all-MiniLM-L6-v2 (384 dim, 90MB, fast)
                            </MenuItem>
                            <MenuItem value="sentence-transformers/all-mpnet-base-v2">
                              all-mpnet-base-v2 (768 dim, 400MB, accurate)
                            </MenuItem>
                            <MenuItem value="sentence-transformers/paraphrase-multilingual-MiniLM-L12-v2">
                              multilingual-MiniLM-L12-v2 (384 dim, multilingual)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <>
                          <TextField
                            label="OpenAI API Key"
                            type="password"
                            value={kbSettings.openai_api_key || ''}
                            onChange={(e) => setKbSettings({ ...kbSettings, openai_api_key: e.target.value })}
                            fullWidth
                            helperText={kbSettings.has_openai_key ? "API key is configured" : "Enter your OpenAI API key"}
                          />
                          <TextField
                            label="OpenAI Model"
                            value={kbSettings.openai_model || 'text-embedding-ada-002'}
                            onChange={(e) => setKbSettings({ ...kbSettings, openai_model: e.target.value })}
                            fullWidth
                            disabled
                          />
                        </>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Max Chunk Tokens"
                        type="number"
                        value={kbSettings.max_chunk_tokens}
                        onChange={(e) => setKbSettings({ ...kbSettings, max_chunk_tokens: parseInt(e.target.value) })}
                        fullWidth
                        inputProps={{ min: 128, max: 2048 }}
                        helperText="Maximum tokens per text chunk"
                      />

                      <TextField
                        label="Chunk Overlap Tokens"
                        type="number"
                        value={kbSettings.chunk_overlap_tokens}
                        onChange={(e) => setKbSettings({ ...kbSettings, chunk_overlap_tokens: parseInt(e.target.value) })}
                        fullWidth
                        inputProps={{ min: 0, max: 512 }}
                        helperText="Overlap between chunks for context"
                      />

                      <TextField
                        label="Batch Size"
                        type="number"
                        value={kbSettings.batch_size}
                        onChange={(e) => setKbSettings({ ...kbSettings, batch_size: parseInt(e.target.value) })}
                        fullWidth
                        inputProps={{ min: 1, max: 100 }}
                        helperText="Number of chunks to process at once"
                      />

                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          Embedding dimension: {kbSettings.embedding_dimension} |
                          {kbSettings.last_updated && ` Last updated: ${new Date(kbSettings.last_updated).toLocaleString()}`}
                        </Typography>
                      </Alert>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={kbSaving ? <CircularProgress size={16} /> : <Save />}
                      onClick={saveKBSettings}
                      disabled={kbSaving}
                    >
                      {kbSaving ? 'Saving...' : 'Save Knowledge Base Settings'}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration Help */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Configuration Guide"
              avatar={<Code color="primary" />}
            />
            <CardContent>
              <Typography variant="body2" paragraph>
                <strong>MCP Server Configuration Format:</strong>
              </Typography>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <pre style={{ margin: 0 }}>
{`{
  "command": "npx",
  "args": ["-y", "@modelcontextprotocol/server-postgres"],
  "env": {
    "POSTGRES_CONNECTION_STRING": "postgresql://user:pass@localhost/db"
  }
}`}
                </pre>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The command will be executed to start the MCP server. Environment variables can be used for configuration.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* MCP Server Dialog */}
      <Dialog open={showMcpDialog} onClose={() => setShowMcpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMcp ? 'Edit MCP Server' : 'Add MCP Server'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Server Name"
              value={mcpForm.name}
              onChange={(e) => setMcpForm({ ...mcpForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={mcpForm.description}
              onChange={(e) => setMcpForm({ ...mcpForm, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Configuration (JSON)"
              value={mcpForm.config}
              onChange={(e) => setMcpForm({ ...mcpForm, config: e.target.value })}
              multiline
              rows={10}
              fullWidth
              required
              helperText="MCP server configuration in JSON format"
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMcpDialog(false)}>Cancel</Button>
          <Button onClick={saveMcpServer} variant="contained">
            {editingMcp ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoPilotSettingsTab;
